import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { downloadImpactPdf, downloadImpactXlsx, impactQuery, type ImpactRow, type ImpactSummary } from '../../api/admImpact'
import { toast } from '../../ui/notify'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { Forbidden } from '../../ui/Forbidden'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'

/**
 * ADM -> Impact, din `views/version-3/softsee/adm/impact.php`.
 *
 * CELE PATRU CIFRE au clasele de culoare pe care app.css chiar le defineste -
 * in portal erau `dashboard-stat` fara clasa, si platforma nu picteaza nimic
 * pentru asta: patru cutii albe. Cifra celor critice isi alege singura culoarea,
 * fiindca o dependenta critica e alta discutie decat niciuna.
 *
 * RANDUL INTREG POARTA STAREA, nu doar pastila din coloana Health: lista se
 * citeste dintr-o privire - „cine e atins" - iar o pastila mica intr-a cincea
 * coloana cere sa fie cautata rand cu rand.
 *
 * CELE DOUA EXPORTURI (Excel, PDF) inca nu s-au mutat: amandoua trec prin
 * `GenerateExcel` si prin generatorul de PDF al portalului, si nu se pot muta pe
 * jumatate. Butoanele nu se deseneaza deloc - un buton care nu face nimic e mai
 * rau decat unul care lipseste.
 */

const STYLES = ['css/adm.css']

const ROW_CLASS: Record<number, string> = { 3: 'adm-row-down', 2: 'adm-row-degraded' }

const HEALTH_PILL: Record<number, string> = { 1: 'adm-pill-ok', 2: 'adm-pill-warn', 3: 'adm-pill-bad' }

function Tiles({ summary }: { summary: ImpactSummary }) {
  return (
    <div className="row adm-tiles">
      <div className="col-md-3 col-sm-6">
        <div className="dashboard-stat blue-madison">
          <div className="visual">
            <i className="fa fa-cubes"></i>
          </div>
          <div className="details">
            <div className="number">{summary.total}</div>
            <div className="desc">Items affected</div>
          </div>
        </div>
      </div>
      <div className="col-md-3 col-sm-6">
        <div className={`dashboard-stat ${summary.critical > 0 ? 'red-intense' : 'grey'}`}>
          <div className="visual">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="details">
            <div className="number">{summary.critical}</div>
            <div className="desc">Of them critical</div>
          </div>
        </div>
      </div>
      <div className="col-md-3 col-sm-6">
        <div className="dashboard-stat green-haze">
          <div className="visual">
            <i className="fas fa-concierge-bell"></i>
          </div>
          <div className="details">
            <div className="number">{summary.services}</div>
            <div className="desc">Services affected</div>
          </div>
        </div>
      </div>
      <div className="col-md-3 col-sm-6">
        <div className="dashboard-stat brown">
          <div className="visual">
            <i className="fas fa-building"></i>
          </div>
          <div className="details">
            <div className="number">{summary.companies}</div>
            <div className="desc">Customers affected</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function Chain({ rows }: { rows: ImpactRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="alert alert-success">
        <i className="fas fa-check-circle"></i> Nothing else depends on this CI. Either it is genuinely standalone, or
        discovery has not yet found what uses it.
      </div>
    )
  }

  return (
    <table className="table table-striped table-hover" id="admImpactTable">
      <thead>
        <tr>
          <th>CI</th>
          <th>Class</th>
          <th>Company</th>
          <th className="text-center">Importance</th>
          {/* Coloana care face ecranul sa raspunda si la a doua intrebare: nu
              doar "ce s-ar strica daca ar pica", ci si "ce e oprit chiar acum". */}
          <th className="text-center">Health</th>
          <th className="text-center">Steps away</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.ciId} className={ROW_CLASS[row.health] ?? ''}>
            <td>{row.name}</td>
            <td>{row.className}</td>
            <td>{row.companyName}</td>
            <td className="text-center">
              <span className={`adm-imp adm-imp-${row.criticality ?? 0}`}>{row.criticalityLabel}</span>
            </td>
            <td className="text-center">
              <span className={`adm-pill ${HEALTH_PILL[row.health] ?? 'adm-pill-ok'}`}>{row.healthLabel}</span>
            </td>
            <td className="text-center">{row.depth}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function ImpactPage({ ciId }: { ciId: number }) {
  const shell = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shell.data?.version ?? '')

  const page = useQuery(impactQuery(ciId))
  const [busy, setBusy] = useState<'' | 'xlsx' | 'pdf'>('')

  /* Excel / PDF - impact.php le arata numai cand chiar e ceva de pus in ele. */
  const save = async (what: 'xlsx' | 'pdf') => {
    setBusy(what)
    try {
      await (what === 'xlsx' ? downloadImpactXlsx(ciId) : downloadImpactPdf(ciId))
    } catch (error) {
      toast((error as Error).message, 'Impact', 'error')
    } finally {
      setBusy('')
    }
  }

  if (page.isError) {
    const status = (page.error as { status?: number }).status
    if (status === 403) return <Forbidden />
    return (
      <Content>
        <div className="alert alert-warning" style={{ margin: 16 }}>
          <i className="fas fa-exclamation-triangle"></i> {(page.error as Error).message}
        </div>
      </Content>
    )
  }

  const data = page.data
  const summary = data?.summary
  const tone = summary && summary.critical > 0 ? 'red-intense' : 'blue-hoki'

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone={tone}
          icon="fa-bolt"
          title={data ? `What depends on ${data.ci.name}` : 'Impact'}
          note="everything that would be affected if this stopped working, directly or through something else"
          actions={
            data ? (
              <>
                <a className="btn btn-default btn-sm" href={`/app/adm/map?root=${data.ci.id}`}>
                  <i className="fas fa-project-diagram"></i> See it on the map
                </a>
                {data.affected.length > 0 ? (
                  <>
                    {' '}
                    <button type="button" className="btn btn-default btn-sm" id="admImpactXlsx" disabled={busy !== ''} onClick={() => void save('xlsx')}>
                      <i className="fas fa-file-excel"></i> {busy === 'xlsx' ? 'Preparing…' : 'Excel'}
                    </button>
                    {data.pdfAvailable ? (
                      <>
                        {' '}
                        <button type="button" className="btn btn-default btn-sm" id="admImpactPdf" disabled={busy !== ''} onClick={() => void save('pdf')}>
                          <i className="fas fa-file-pdf"></i> {busy === 'pdf' ? 'Preparing…' : 'PDF'}
                        </button>
                      </>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : undefined
          }
        />

        {summary ? <Tiles summary={summary} /> : null}

        <div className="row">
          <div className="col-md-12">
            <div className="adm-card">
              <div className="adm-card-head">
                <h5>
                  <i className="fas fa-sitemap"></i> The dependency chain
                </h5>
                <span className="adm-card-note">nearest first</span>
              </div>
              <div className="adm-card-body">{data ? <Chain rows={data.affected} /> : null}</div>
            </div>
          </div>
        </div>
      </div>
    </Content>
  )
}
